import React, { useState, useEffect, createContext, useContext } from 'react'
import { getApiUrl, createFetchConfig } from '../config/api'

interface SystemSettingsData {
  [key: string]: {
    value: string
    category: string
    description: string
    updatedAt: string
    updatedBy: string
  }
}

interface SystemSettingsContextType {
  settings: SystemSettingsData
  loading: boolean
  error: string | null
  refetch: () => void
  getSetting: (key: string, defaultValue?: string) => string
}

const SystemSettingsContext = createContext<SystemSettingsContextType | undefined>(undefined)

export const useSystemSettings = () => {
  const context = useContext(SystemSettingsContext)
  if (context === undefined) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider')
  }
  return context
}

// Hook for fetching system settings
export const useSystemSettingsData = () => {
  const [settings, setSettings] = useState<SystemSettingsData>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(getApiUrl('/system/settings'), createFetchConfig())
      const data = await response.json()
      
      if (data.success) {
        setSettings(data.data)
      } else {
        setError(data.message || '載入設定失敗')
      }
    } catch (err) {
      console.error('Fetch settings error:', err)
      setError('載入設定失敗')
    } finally {
      setLoading(false)
    }
  }

  const getSetting = (key: string, defaultValue: string = ''): string => {
    return settings[key]?.value || defaultValue
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings,
    getSetting
  }
}

// Provider component
export const SystemSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const settingsData = useSystemSettingsData()
  
  // 動態更新 HTML title
  useEffect(() => {
    const systemTitle = settingsData.getSetting('system.title', 'MoniJira')
    document.title = `${systemTitle} - 專案管理工具`
  }, [settingsData.settings, settingsData.getSetting])
  
  return (
    <SystemSettingsContext.Provider value={settingsData}>
      {children}
    </SystemSettingsContext.Provider>
  )
}

export default useSystemSettings